import React, { useState, useRef, useMemo, useLayoutEffect } from "react";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  Download,
  Eraser,
  Info,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHF = (v) =>
  new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Math.max(0, v || 0));

const input = "w-full px-4 py-3 rounded-xl border border-ink-700/15 bg-white text-ink-900 text-base focus:outline-none focus:ring-2 focus:ring-gold-500/50";

/* ─── Sub-components (defined OUTSIDE Vertrag to preserve focus on inputs) ─── */
const Card = ({ children }) => (
  <div className="bg-white rounded-3xl border border-ink-700/10 p-6 lg:p-10 shadow-sm min-h-[400px]">
    {children}
  </div>
);

const StepTitle = ({ children, sub }) => (
  <div className="mb-8">
    <h3 className="font-display text-2xl lg:text-3xl font-medium text-ink-900">{children}</h3>
    {sub && <p className="mt-2 text-sm text-ink-500">{sub}</p>}
  </div>
);

const Progress = ({ step, total, title }) => (
  <div className="mb-8">
    <div className="flex justify-between text-[10px] uppercase tracking-widest text-ink-500 mb-2">
      <span>Schritt {step + 1} / {total}</span>
      <span className="text-gold-700 font-semibold">{title}</span>
    </div>
    <div className="h-1.5 bg-ivory-200 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-gold-400 to-gold-600 transition-all duration-500"
        style={{ width: `${((step + 1) / total) * 100}%` }}
      />
    </div>
  </div>
);

/* ─── Wizard step definitions ─── */
const STEPS = [
  { id: "start", title: "Start" },
  { id: "vertragsart", title: "Vertragsart" },
  { id: "person", title: "Persönliche Daten" },
  { id: "kyc", title: "Vermögens-Check" },
  { id: "allokation", title: "Allokation" },
  { id: "zahlung", title: "Zahlungsplan" },
  { id: "bank", title: "Bank & AGB" },
  { id: "datenschutz", title: "Datenschutz" },
  { id: "kyc2", title: "Ausweis & Herkunft" },
  { id: "widerruf", title: "Widerruf" },
  { id: "unterschrift", title: "Unterschrift" },
  { id: "done", title: "Fertig" },
];

const Vertrag = ({ onClose }) => {
  const { authHeader } = useAuth();
  const [step, setStep] = useState(0);
  // Initial state uses strings for numeric fields so users can freely clear them
  const [data, setData] = useState({
    // Vertragsart – both KV and SV always
    // Personal
    geschlecht: "m",
    vorname: "",
    nachname: "",
    geburtsname: "",
    geburtsdatum: "",
    nationalitaet: "",
    familienstand: "",
    land: "CH",
    plz: "",
    wohnort: "",
    strasse: "",
    telefon: "",
    email: "",
    beruf: "",
    kinder: "",
    // KYC
    verm_anteil: "<25",
    reserven: ">6",
    zweck: [],
    // Allokation (strings)
    allok_typ: "regel",
    allok_gold: "",
    allok_silber: "",
    allok_platin: "",
    allok_palladium: "",
    // Zahlung (strings so they can be cleared)
    einzahlungsdauer: "12",
    einmalzahlung: "",
    monatszahlung: "100",
    // Bank
    bank_name: "",
    bank_iban: "",
    bank_bic: "",
    agb_akzeptiert: false,
    datenschutz_akzeptiert: false,
    // Mittelherkunft
    einkommen_typ: "angestellt",
    firma: "",
    firma_seit: "",
    // Bruttojahreseinkommen (range)
    brutto_range: "",  // "<75", "75-110", "110-150", "150-250", ">250"
    // Nettovermögen (range)
    netto_range: "",   // "<500", "500-750", "750-1200", "1200-2000", ">2000"
    geo_gebiet: "",
    mittelherkunft: [],
    mittelherkunft_weitere: "",
    nachweis_art: "",
    // PEP: two-way choice
    pep_keine: false,   // "Ich bin KEINE PEP"
    pep_ja: false,       // "Ich bin PEP"
    pep_namen: "",
    // Vermögensstruktur (checkbox + %)
    vs_krypto: false, vs_krypto_pct: "",
    vs_immo: false, vs_immo_pct: "",
    vs_kunst: false, vs_kunst_pct: "",
    vs_liquid: false, vs_liquid_pct: "",
    vs_anlagen: false, vs_anlagen_pct: "",
    vs_edelmetalle: false, vs_edelmetalle_pct: "",
    vs_sonstige: false, vs_sonstige_text: "", vs_sonstige_pct: "",
    steuer_id: "",
    steuer_wohnsitz: "",
    geburtsort: "",
    ausweis_art: "",
    ausweis_nr: "",
    ausweis_ausstellung: "",
    ausweis_ablauf: "",
    ausweis_behoerde: "",
    // Widerruf
    widerrufsverzicht: false,
    // Gesprächsprotokoll – 11 Bestätigungen (wortwörtlich aus dem SWM-PDF)
    gp_keine_beratung: false,
    gp_unterlagen_erhalten: false,
    gp_alle_fragen_beantwortet: false,
    gp_zufrieden_praesentation: false,
    gp_eigener_wunsch: false,
    gp_mindestlaufzeit: false,
    gp_kosten_hinweis: false,
    gp_agio_bekannt: false,
    gp_einrichtungskosten_bekannt: false,
    gp_agb_kosten_bestaetigt: false,
    gp_rate_finanziell_moeglich: false,
    stornierung_nahegelegt: "nein",
    stornierung_produkte: [{ produkt: "", vertragsart: "", entscheidung: "" }],
    weiterempfehlung_ja: false,
    weiterempfehlung_bestaetigt: false,
    gesprachsdatum: "",
    gp_anwesende: "",
    gp_bemerkungen: "",
    // Weitere Geschäftspartner (optional, kommt auf Seite 18 bei "Weitere Geschäftspartner")
    vp2_name: "",
    vp2_nr: "",
    // Empfehlungen – dynamic array (up to 7)
    empfehlungen: [{
      name: "", vorname: "", alter: "", familienstand: "", kinder: "",
      beruf: "", adresse: "", adresse2: "", telefon: "", hobbys: "", gemeinsamkeiten: "",
    }],
    // Unterschrift
    ort: "",
    datum: new Date().toISOString().slice(0, 10).split("-").reverse().join("."),
    vertragsnummer: "",
    vp_name: "Pascal Schwab",
    vp_nr: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [sigDrawn, setSigDrawn] = useState(false);
  const custSigRef = useRef(null);
  const vpSigRef = useRef(null);

  // Sync signature canvas actual pixel size with displayed CSS size
  // to fix mouse/touch offset when the canvas is CSS-stretched.
  useLayoutEffect(() => {
    if (STEPS[step].id !== "unterschrift") return;
    const resize = (sigRef, heightPx) => {
      const canvas = sigRef.current?.getCanvas();
      if (!canvas) return () => {};
      const parent = canvas.parentElement;
      const apply = () => {
        const w = Math.floor(parent.getBoundingClientRect().width);
        canvas.width = w;
        canvas.height = heightPx;
        canvas.style.width = w + "px";
        canvas.style.height = heightPx + "px";
        sigRef.current?.clear?.();
      };
      apply();
      const ro = new ResizeObserver(apply);
      ro.observe(parent);
      return () => ro.disconnect();
    };
    const c1 = resize(custSigRef, 200);
    const c2 = resize(vpSigRef, 120);
    return () => { c1?.(); c2?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setNum = (k, v) => setData((d) => ({ ...d, [k]: v }));  // keep raw string; parse when computing
  const toggle = (k, val) =>
    setData((d) => {
      const arr = d[k] || [];
      return { ...d, [k]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });

  const numOf = (v) => Number(v) || 0;
  const allokSum =
    numOf(data.allok_gold) + numOf(data.allok_silber) + numOf(data.allok_platin) + numOf(data.allok_palladium);

  /* ─── Calculated payments ─── */
  const calc = useMemo(() => {
    const einmal = numOf(data.einmalzahlung);
    const monat = numOf(data.monatszahlung);
    const dauer = numOf(data.einzahlungsdauer);
    const agio1 = einmal * 0.05;
    const gesamt1 = einmal + agio1;
    const monatssumme = monat * 12 * dauer;
    const agio2 = monatssumme * 0.05;
    const monatsgesamt = monatssumme + agio2;
    const zahlungssumme = einmal + monatssumme;
    const gesamt = gesamt1 + monatsgesamt;
    return { einmal, monat, dauer, agio1, gesamt1, monatssumme, agio2, monatsgesamt, zahlungssumme, gesamt };
  }, [data.einmalzahlung, data.monatszahlung, data.einzahlungsdauer]);

  const canNext = () => {
    const s = STEPS[step].id;
    if (s === "person") return data.vorname && data.nachname && data.email && data.strasse && data.plz && data.wohnort;
    if (s === "allokation" && data.allok_typ === "eigen") return allokSum === 100;
    if (s === "zahlung") return calc.einmal + calc.monat > 0 && calc.dauer >= 12 && calc.dauer <= 40;
    if (s === "bank") return data.bank_iban && data.agb_akzeptiert;
    if (s === "datenschutz") return data.datenschutz_akzeptiert;
    if (s === "unterschrift") return data.ort && data.datum && sigDrawn && !custSigRef.current?.isEmpty();
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const sig = custSigRef.current?.getCanvas().toDataURL("image/png");
      const vpSig = vpSigRef.current && !vpSigRef.current.isEmpty()
        ? vpSigRef.current.getCanvas().toDataURL("image/png") : "";
      const res = await axios.post(`${API}/vertrag/submit`, {
        data,
        signature: sig,
        vermittler_signature: vpSig,
      }, { headers: authHeader() });
      setResult(res.data);
      setStep(STEPS.length - 1);
    } catch (e) {
      setError(e.response?.data?.detail || "Fehler beim Absenden.");
    } finally {
      setSubmitting(false);
    }
  };

  const step_id = STEPS[step].id;

  return (
    <div className="mt-14 border-t border-ink-700/10 pt-14" data-testid="vertrag-wizard">
      <div className="text-xs uppercase tracking-[0.22em] font-bold text-gold-700 mb-3">
        Kaufantrag &amp; Servicevertrag
      </div>
      <h2 className="font-display text-3xl lg:text-4xl font-medium mb-2">
        Bist du bereit, für deine Zukunft vorzusorgen?
      </h2>
      <p className="text-ink-500 mb-8 max-w-2xl">
        Ein solides Fundament aus Gold, Silber, Platin und Palladium – unabhängig
        von Zentralbanken, unabhängig von Krisen. Für dich, deine Familie und die
        Momente, die wirklich zählen. Wir begleiten dich Schritt für Schritt.
      </p>

      <Progress step={step} total={STEPS.length} title={STEPS[step].title} />

      <Card>
        {/* STEP 0: Start */}
        {step_id === "start" && (
          <div className="text-center py-10">
            <FileText className="w-14 h-14 mx-auto mb-6 text-gold-600" />
            <h3 className="font-display text-2xl mb-3">Bereit, den Vertrag auszufüllen?</h3>
            <p className="text-ink-500 max-w-lg mx-auto">
              Du füllst alles online aus, unterschreibst mit dem Finger/Maus – wir kümmern uns um das
              Ausfüllen und Versenden des offiziellen PDFs.
            </p>
          </div>
        )}

        {/* STEP 1: Vertragsart */}
        {step_id === "vertragsart" && (
          <>
            <StepTitle sub="Beide Verträge werden immer zusammen abgeschlossen: Kaufvertrag (KV) + Servicevertrag (SV mit STRATEGIC ETERNAL VALUE).">
              Vertragsart
            </StepTitle>
            <div className="grid gap-4">
              <div className="p-5 rounded-2xl bg-ivory-100 border border-gold-500/30 flex items-start gap-4">
                <div className="w-6 h-6 rounded bg-gold-500 flex items-center justify-center mt-1"><Check className="w-4 h-4 text-ink-900" strokeWidth={3} /></div>
                <div>
                  <div className="font-semibold text-ink-900">Kaufvertrag (KV)</div>
                  <div className="text-sm text-ink-500">Erwerb der Edelmetalle auf eigenen Namen und Rechnung.</div>
                </div>
              </div>
              <div className="p-5 rounded-2xl bg-ivory-100 border border-gold-500/30 flex items-start gap-4">
                <div className="w-6 h-6 rounded bg-gold-500 flex items-center justify-center mt-1"><Check className="w-4 h-4 text-ink-900" strokeWidth={3} /></div>
                <div>
                  <div className="font-semibold text-ink-900">Servicevertrag (SV) · STRATEGIC ETERNAL VALUE</div>
                  <div className="text-sm text-ink-500">Verwahrung, Versicherung und laufende Verwaltung der Edelmetalle.</div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* STEP 2: Persönliche Daten */}
        {step_id === "person" && (
          <>
            <StepTitle>Persönliche Angaben</StepTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-ink-900 font-medium">Geschlecht</label>
                <select className={input + " mt-1"} value={data.geschlecht} onChange={(e) => set("geschlecht", e.target.value)} data-testid="input-geschlecht">
                  <option value="m">Männlich</option>
                  <option value="w">Weiblich</option>
                  <option value="d">Divers</option>
                </select>
              </div>
              <div />
              <div><label className="text-sm">Vorname *</label><input className={input} value={data.vorname} onChange={(e) => set("vorname", e.target.value)} data-testid="input-vorname" /></div>
              <div><label className="text-sm">Nachname *</label><input className={input} value={data.nachname} onChange={(e) => set("nachname", e.target.value)} data-testid="input-nachname" /></div>
              <div><label className="text-sm">Geburtsname (optional)</label><input className={input} value={data.geburtsname} onChange={(e) => set("geburtsname", e.target.value)} /></div>
              <div><label className="text-sm">Geburtsdatum</label><input className={input} placeholder="TT.MM.JJJJ" value={data.geburtsdatum} onChange={(e) => set("geburtsdatum", e.target.value)} /></div>
              <div><label className="text-sm">Nationalität</label><input className={input} value={data.nationalitaet} onChange={(e) => set("nationalitaet", e.target.value)} /></div>
              <div><label className="text-sm">Familienstand</label><input className={input} value={data.familienstand} onChange={(e) => set("familienstand", e.target.value)} /></div>
              <div><label className="text-sm">Straße & Hausnr. *</label><input className={input} value={data.strasse} onChange={(e) => set("strasse", e.target.value)} data-testid="input-strasse" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-sm">Land</label><input className={input} value={data.land} onChange={(e) => set("land", e.target.value)} data-testid="input-land" /></div>
                <div><label className="text-sm">PLZ *</label><input className={input} value={data.plz} onChange={(e) => set("plz", e.target.value)} data-testid="input-plz" /></div>
                <div className="col-span-1"><label className="text-sm">Ort *</label><input className={input} value={data.wohnort} onChange={(e) => set("wohnort", e.target.value)} data-testid="input-wohnort" /></div>
              </div>
              <div><label className="text-sm">Telefon</label><input className={input} value={data.telefon} onChange={(e) => set("telefon", e.target.value)} /></div>
              <div><label className="text-sm">E-Mail *</label><input type="email" className={input} value={data.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" /></div>
              <div><label className="text-sm">Beruf</label><input className={input} value={data.beruf} onChange={(e) => set("beruf", e.target.value)} /></div>
              <div><label className="text-sm">Geburtsjahr(e) der Kinder</label><input className={input} placeholder="z. B. 2010, 2015" value={data.kinder} onChange={(e) => set("kinder", e.target.value)} /></div>
            </div>
          </>
        )}

        {/* STEP 3: KYC Fragen 1-2, 4 */}
        {step_id === "kyc" && (
          <>
            <StepTitle sub="Wir müssen kurz deine finanzielle Situation prüfen (Pflicht laut Geldwäschegesetz).">
              Vermögens-Check
            </StepTitle>
            <div className="space-y-6">
              <div>
                <div className="text-sm font-medium mb-3">1. Welchen Anteil an deinem verfügbaren Vermögen macht der Erwerb aus?</div>
                {[["<25", "weniger als 25 %"], ["25-50", "zwischen 25 % und 50 %"], [">50", "mehr als 50 %"]].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-3 py-2 cursor-pointer">
                    <input type="radio" name="verm" checked={data.verm_anteil === v} onChange={() => set("verm_anteil", v)} className="accent-gold-600" />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
              <div>
                <div className="text-sm font-medium mb-3">2. Reserven nach dem Erwerb reichen mir …</div>
                {[[">6", "mehr als 6 Monate"], ["<6", "weniger als 6 Monate"], [">3", "mehr als 3 Monate"]].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-3 py-2 cursor-pointer">
                    <input type="radio" name="res" checked={data.reserven === v} onChange={() => set("reserven", v)} className="accent-gold-600" />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
              <div>
                <div className="text-sm font-medium mb-3">4. Zweck (Mehrfachauswahl möglich)</div>
                {[["inflation", "wirtschaftliche Situation absichern (z. B. Inflation)"], ["vorsorge", "Vorsorge für Kinder und Enkel"], ["sparziel", "auf ein bestimmtes Ziel sparen"]].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-3 py-2 cursor-pointer">
                    <input type="checkbox" checked={data.zweck.includes(v)} onChange={() => toggle("zweck", v)} className="accent-gold-600 w-4 h-4" />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
              <div className="text-xs text-ink-500 bg-ivory-100 rounded-xl p-4 flex gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gold-600" />
                <span>Uns ist bewusst, dass Edelmetalle keine Zinserträge bringen und Kursschwankungen unterliegen. Der Erwerb eignet sich ergänzend zur Altersvorsorge.</span>
              </div>
            </div>
          </>
        )}

        {/* STEP 4: Allokation */}
        {step_id === "allokation" && (
          <>
            <StepTitle sub="Aufteilung der Edelmetalle (Regelallokation oder eigene Wunsch-Verteilung).">
              Allokation
            </StepTitle>
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              <button type="button" onClick={() => set("allok_typ", "regel")} className={`text-left rounded-2xl p-5 border-2 transition ${data.allok_typ === "regel" ? "border-gold-500 bg-ivory-100" : "border-ink-700/10"}`}>
                <div className="font-semibold mb-1">Regelallokation</div>
                <div className="text-sm text-ink-500">SWM verteilt automatisch nach ihrem bewährten Konzept.</div>
              </button>
              <button type="button" onClick={() => set("allok_typ", "eigen")} className={`text-left rounded-2xl p-5 border-2 transition ${data.allok_typ === "eigen" ? "border-gold-500 bg-ivory-100" : "border-ink-700/10"}`} data-testid="allok-eigen">
                <div className="font-semibold mb-1">Eigenallokation</div>
                <div className="text-sm text-ink-500">Du wählst selbst (Summe muss 100 % ergeben, Schritte à 5 %).</div>
              </button>
            </div>
            {data.allok_typ === "eigen" && (
              <div className="rounded-2xl bg-ivory-100 p-5">
                {[["allok_gold", "Gold"], ["allok_silber", "Silber"], ["allok_platin", "Platin"], ["allok_palladium", "Palladium"]].map(([k, l]) => (
                  <div key={k} className="flex items-center gap-4 py-2">
                    <label className="w-24 text-sm font-medium">{l}</label>
                    <input type="number" step="5" min="0" max="100" value={data[k]} onChange={(e) => set(k, Math.max(0, Math.min(100, +e.target.value || 0)))} className={input + " max-w-[100px]"} data-testid={`input-${k}`} />
                    <span className="text-sm text-ink-500">%</span>
                  </div>
                ))}
                <div className={`mt-3 p-3 rounded-lg text-sm ${allokSum === 100 ? "bg-gold-100 text-gold-700" : "bg-red-50 text-red-700"}`}>
                  Summe: <strong>{allokSum} %</strong> {allokSum === 100 ? "✓" : `(muss exakt 100 % sein)`}
                </div>
              </div>
            )}
          </>
        )}

        {/* STEP 5: Zahlung */}
        {step_id === "zahlung" && (
          <>
            <StepTitle sub="Deine Einzahlungen (Einmal & Monat) und die Laufzeit von 12–40 Jahren. Wir berechnen die 5 % Agio automatisch.">
              Zahlungsplan
            </StepTitle>
            <div className="mb-5 text-xs text-ink-500 bg-ivory-100 rounded-xl p-4 flex gap-2">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gold-600" />
              <span>
                <strong>Wichtig:</strong> Die Laufzeiten sind gemäß SWM in der Regel langfristig, <strong>mindestens 12 Jahre</strong>.
                Mit deiner Auswahl bist du mit der vereinbarten Vertragsdauer einverstanden.
                Auf die Einmal- und Monatszahlung fällt jeweils <strong>5 % Agio</strong> an.
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div><label className="text-sm">Einmalzahlung (€)</label><input type="number" step="100" min="0" value={data.einmalzahlung} onChange={(e) => set("einmalzahlung", e.target.value)} className={input} data-testid="input-einmalzahlung" /></div>
              <div><label className="text-sm">Monatszahlung (€)</label><input type="number" step="50" min="0" value={data.monatszahlung} onChange={(e) => set("monatszahlung", e.target.value)} className={input} data-testid="input-monatszahlung" /></div>
              <div><label className="text-sm">Einzahlungsdauer (Jahre, 12-40)</label><input type="number" min="12" max="40" value={data.einzahlungsdauer} onChange={(e) => set("einzahlungsdauer", e.target.value)} className={input} data-testid="input-dauer" /></div>
            </div>
            <div className="rounded-2xl bg-ink-900 text-ivory-100 p-6 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-gold-300 font-semibold mb-2">Berechnung</div>
              <div className="grid grid-cols-2 gap-y-1 text-sm">
                <div className="text-ivory-100/60">Einmalzahlung</div><div className="text-right">{CHF(calc.einmal)}</div>
                <div className="text-ivory-100/60">+ 5 % Agio</div><div className="text-right text-gold-300">{CHF(calc.agio1)}</div>
                <div className="text-ivory-100/60">= Gesamt-Einmalzahlung</div><div className="text-right font-semibold">{CHF(calc.gesamt1)}</div>
                <div className="col-span-2 border-t border-gold-500/20 my-2" />
                <div className="text-ivory-100/60">Monatszahlung × 12 × {calc.dauer} Jahre</div><div className="text-right">{CHF(calc.monatssumme)}</div>
                <div className="text-ivory-100/60">+ 5 % Agio</div><div className="text-right text-gold-300">{CHF(calc.agio2)}</div>
                <div className="text-ivory-100/60">= Gesamt monatl. Zahlungssumme</div><div className="text-right font-semibold">{CHF(calc.monatsgesamt)}</div>
                <div className="col-span-2 border-t border-gold-500/20 my-2" />
                <div className="text-ivory-100/60">Zahlungssumme (exkl. Agio)</div><div className="text-right">{CHF(calc.zahlungssumme)}</div>
                <div className="text-lg text-gold-gradient font-display mt-2">Gesamtzahlungssumme (inkl. Agio)</div><div className="text-right text-lg text-gold-gradient font-display font-medium mt-2" data-testid="calc-gesamt">{CHF(calc.gesamt)}</div>
              </div>
            </div>
          </>
        )}

        {/* STEP 6: Bank + AGB */}
        {step_id === "bank" && (
          <>
            <StepTitle sub="Wohin sollen Auszahlungen gehen? Und akzeptiere bitte die AGB.">
              Bankverbindung &amp; AGB
            </StepTitle>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div><label className="text-sm">Bank</label><input className={input} value={data.bank_name} onChange={(e) => set("bank_name", e.target.value)} /></div>
              <div><label className="text-sm">BIC / SWIFT</label><input className={input} value={data.bank_bic} onChange={(e) => set("bank_bic", e.target.value)} /></div>
              <div className="md:col-span-2"><label className="text-sm">IBAN *</label><input className={input} value={data.bank_iban} onChange={(e) => set("bank_iban", e.target.value)} data-testid="input-iban" /></div>
            </div>
            <div className="rounded-2xl bg-ivory-100 p-5 flex items-start gap-3">
              <FileText className="w-5 h-5 text-gold-600 mt-1" />
              <div className="flex-1">
                <div className="font-semibold text-ink-900">Allgemeine Geschäftsbedingungen (AGB)</div>
                <a
                  href="https://customer-assets-lqy194kg.emergentagent.net/job_finance-makeover-2/artifacts/5mu1ce02_AGB_SWM.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="agb-download"
                  className="inline-flex items-center gap-2 text-sm text-gold-700 underline mt-1"
                >
                  AGB als PDF herunterladen
                </a>
                <label className="flex items-center gap-3 mt-4 cursor-pointer">
                  <input type="checkbox" checked={data.agb_akzeptiert} onChange={(e) => set("agb_akzeptiert", e.target.checked)} className="accent-gold-600 w-4 h-4" data-testid="agb-akzeptiert" />
                  <span className="text-sm">Ich habe die AGB gelesen und akzeptiere sie.</span>
                </label>
              </div>
            </div>
          </>
        )}

        {/* STEP 7: Datenschutz */}
        {step_id === "datenschutz" && (
          <>
            <StepTitle sub="Ich stimme der Datenverarbeitung gemäss SWM-Datenschutzerklärung als Antragsteller (1) zu.">
              Datenschutz-Zustimmung
            </StepTitle>
            <div className="rounded-2xl bg-ivory-100 p-6 text-sm text-ink-700 leading-relaxed">
              Mit deiner Bestätigung erklärst du:
              <ul className="list-disc list-inside mt-3 space-y-2">
                <li>Kenntnisnahme der Datenschutzinformationen der SWM AG.</li>
                <li>Einwilligung in die Verarbeitung meiner personenbezogenen Daten zum Zweck der Vertragsabwicklung.</li>
              </ul>
              <div className="mt-5 pt-5 border-t border-ink-700/10 flex items-start gap-3">
                <FileText className="w-5 h-5 text-gold-600 mt-0.5 flex-shrink-0" />
                <div>
                  <a
                    href="https://customer-assets-lqy194kg.emergentagent.net/job_finance-makeover-2/artifacts/iiiofvlg_Datenschutz.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="datenschutz-download"
                    className="text-sm text-gold-700 underline"
                  >
                    Datenschutzerklärung als PDF herunterladen
                  </a>
                </div>
              </div>
              <label className="mt-5 pt-5 border-t border-ink-700/10 flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.datenschutz_akzeptiert}
                  onChange={(e) => set("datenschutz_akzeptiert", e.target.checked)}
                  className="accent-gold-600 w-4 h-4"
                  data-testid="datenschutz-akzeptiert"
                />
                <span className="text-sm">Ich habe die Datenschutzerklärung gelesen und akzeptiere sie.</span>
              </label>
            </div>
          </>
        )}

        {/* STEP 8: Ausweis & Mittelherkunft */}
        {step_id === "kyc2" && (
          <>
            <StepTitle sub="Pflichtangaben zur Identifizierung, Vermögenssituation und Herkunft der Mittel (Geldwäscheprävention).">
              Ausweis, Vermögen &amp; Mittelherkunft
            </StepTitle>
            <div className="mb-5 text-xs text-ink-500 bg-ivory-100 rounded-xl p-4 flex gap-2">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gold-600" />
              <span>
                Diese Angaben sind gesetzlich vorgeschrieben (Geldwäscheprävention nach GwG). Als
                <strong> PEP (politisch exponierte Person)</strong> gilt, wer ein wichtiges öffentliches
                Amt ausübt oder ausgeübt hat – bzw. eng damit verbundene Personen. Deine Mittelherkunft
                wird nur intern zur Nachweiserbringung genutzt.
              </span>
            </div>
            <div className="mb-6"><label className="text-sm">Geburtsort</label><input className={input} value={data.geburtsort || ""} onChange={(e) => set("geburtsort", e.target.value)} /></div>

            <div className="text-sm font-semibold text-ink-900 mb-3 mt-6">Ausweis</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="text-sm">Steuer-ID</label><input className={input} value={data.steuer_id} onChange={(e) => set("steuer_id", e.target.value)} /></div>
              <div><label className="text-sm">Steuerlicher Wohnsitz (falls abweichend)</label><input className={input} value={data.steuer_wohnsitz} onChange={(e) => set("steuer_wohnsitz", e.target.value)} /></div>
              <div><label className="text-sm">Ausweisart (Pass / ID)</label><input className={input} value={data.ausweis_art} onChange={(e) => set("ausweis_art", e.target.value)} /></div>
              <div><label className="text-sm">Ausweisnummer</label><input className={input} value={data.ausweis_nr} onChange={(e) => set("ausweis_nr", e.target.value)} /></div>
              <div><label className="text-sm">Ausstellungsdatum</label><input className={input} value={data.ausweis_ausstellung} onChange={(e) => set("ausweis_ausstellung", e.target.value)} /></div>
              <div><label className="text-sm">Ablaufdatum</label><input className={input} value={data.ausweis_ablauf} onChange={(e) => set("ausweis_ablauf", e.target.value)} /></div>
              <div className="md:col-span-2"><label className="text-sm">Ausstellende Behörde</label><input className={input} value={data.ausweis_behoerde} onChange={(e) => set("ausweis_behoerde", e.target.value)} /></div>
            </div>

            <div className="text-sm font-semibold text-ink-900 mb-3 mt-8">Erwerbstätigkeit</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm">Erwerbsart</label>
                <select className={input} value={data.einkommen_typ} onChange={(e) => set("einkommen_typ", e.target.value)}>
                  <option value="angestellt">Angestellt</option>
                  <option value="inhaber">Inhaber eines Unternehmens</option>
                  <option value="vertreter">Gesetzlicher Vertreter</option>
                  <option value="rente">Rentenbezug</option>
                  <option value="andere">Andere</option>
                </select>
              </div>
              <div><label className="text-sm">Geografisches Gebiet der Erwerbstätigkeit/Rentenbezugs (Land)</label><input className={input} value={data.geo_gebiet} onChange={(e) => set("geo_gebiet", e.target.value)} /></div>
              <div><label className="text-sm">Firma / Arbeitgeber</label><input className={input} value={data.firma} onChange={(e) => set("firma", e.target.value)} data-testid="input-firma" /></div>
              <div><label className="text-sm">Beschäftigt seit</label><input className={input} placeholder="z. B. 03/2020" value={data.firma_seit} onChange={(e) => set("firma_seit", e.target.value)} data-testid="input-firma-seit" /></div>
            </div>

            <div className="text-sm font-semibold text-ink-900 mb-3 mt-8">Wirtschaftliche Situation</div>
            <div className="mb-2 text-xs text-ink-500">Bruttojahreseinkommen (bitte anwählen)</div>
            <div className="grid md:grid-cols-2 gap-2 mb-6">
              {[["<75", "unter 75'000 CHF/EUR"],["75-110","75'000 – 110'000"],["110-150","110'000 – 150'000"],["150-250","150'000 – 250'000"],[">250","über 250'000"]].map(([v,l])=>(
                <label key={v} className="flex items-center gap-2 cursor-pointer text-sm py-1"><input type="radio" name="brutto" checked={data.brutto_range===v} onChange={()=>set("brutto_range",v)} className="accent-gold-600" data-testid={`brutto-${v}`}/><span>{l}</span></label>
              ))}
            </div>
            <div className="mb-2 text-xs text-ink-500">Nettovermögen (bitte anwählen)</div>
            <div className="grid md:grid-cols-2 gap-2">
              {[["<500", "unter 500'000 CHF/EUR"],["500-750","500'000 – 750'000"],["750-1200","750'000 – 1'200'000"],["1200-2000","1'200'000 – 2'000'000"],[">2000","über 2'000'000"]].map(([v,l])=>(
                <label key={v} className="flex items-center gap-2 cursor-pointer text-sm py-1"><input type="radio" name="netto" checked={data.netto_range===v} onChange={()=>set("netto_range",v)} className="accent-gold-600" data-testid={`netto-${v}`}/><span>{l}</span></label>
              ))}
            </div>

            <div className="text-sm font-semibold text-ink-900 mb-3 mt-8">Vermögensstruktur</div>
            <div className="text-xs text-ink-500 mb-3">Welche Vermögenswerte hast du – und (optional) wie viel % davon? Die Summe sollte 100 % ergeben.</div>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                ["vs_krypto", "Kryptowährung"],
                ["vs_immo", "Immobilien"],
                ["vs_kunst", "Kunstgegenstände"],
                ["vs_liquid", "Liquidität"],
                ["vs_anlagen", "Anlagen (Aktien / Fonds)"],
                ["vs_edelmetalle", "Edelmetalle"],
              ].map(([k, l]) => (
                <div key={k} className="flex items-center gap-3 py-1.5">
                  <input type="checkbox" checked={data[k]} onChange={(e) => set(k, e.target.checked)} className="accent-gold-600 w-4 h-4" data-testid={`vs-${k}`} />
                  <label className="text-sm flex-1">{l}</label>
                  {data[k] && (
                    <input type="number" placeholder="%" min="0" max="100" step="5" value={data[`${k}_pct`]} onChange={(e) => set(`${k}_pct`, e.target.value)} className={input + " max-w-[80px]"} data-testid={`vs-${k}-pct`} />
                  )}
                </div>
              ))}
              <div className="flex items-center gap-3 py-1.5 md:col-span-2">
                <input type="checkbox" checked={data.vs_sonstige} onChange={(e) => set("vs_sonstige", e.target.checked)} className="accent-gold-600 w-4 h-4" data-testid="vs-sonstige" />
                <label className="text-sm">Sonstige:</label>
                <input className={input + " flex-1"} placeholder="z. B. Firma, Beteiligungen" value={data.vs_sonstige_text} onChange={(e) => set("vs_sonstige_text", e.target.value)} disabled={!data.vs_sonstige}/>
                {data.vs_sonstige && (
                  <input type="number" placeholder="%" min="0" max="100" step="5" value={data.vs_sonstige_pct} onChange={(e) => set("vs_sonstige_pct", e.target.value)} className={input + " max-w-[80px]"} data-testid="vs-sonstige-pct" />
                )}
              </div>
            </div>
            {(() => {
              const sum = ["vs_krypto","vs_immo","vs_kunst","vs_liquid","vs_anlagen","vs_edelmetalle"]
                .filter((k)=>data[k]).reduce((a,k)=>a+(Number(data[`${k}_pct`])||0),0)
                + (data.vs_sonstige ? (Number(data.vs_sonstige_pct)||0) : 0);
              const anyChecked = ["vs_krypto","vs_immo","vs_kunst","vs_liquid","vs_anlagen","vs_edelmetalle","vs_sonstige"].some((k)=>data[k]);
              if (!anyChecked) return null;
              const ok = sum === 100;
              return (
                <div className={`mt-3 text-xs rounded-lg px-3 py-2 ${ok ? "bg-ivory-100 text-ink-500" : "bg-gold-100/60 text-gold-900 border border-gold-500/40"}`} data-testid="vs-sum-hint">
                  Summe: <span className="font-semibold tabular-nums">{sum}%</span>
                  {!ok && sum > 0 && <span> – bitte auf 100 % anpassen (aktuell {sum > 100 ? "über" : "unter"} 100 %).</span>}
                </div>
              );
            })()}

            <div className="text-sm font-semibold text-ink-900 mb-3 mt-8">Mittelherkunft</div>
            <div className="grid md:grid-cols-2 gap-2">
              {[["einkommen", "Persönliches / familiäres Einkommen"], ["immobilie", "Immobilienverkauf"], ["erbe", "Familienerbe"], ["ersparnisse", "Ersparnisse"], ["schenkung", "Spende / Schenkung"], ["verschuldung", "Verschuldung / Kredit"], ["sonstige", "Sonstige"]].map(([v, l]) => (
                <label key={v} className="flex items-center gap-3 py-1.5 cursor-pointer">
                  <input type="checkbox" checked={data.mittelherkunft.includes(v)} onChange={() => toggle("mittelherkunft", v)} className="accent-gold-600 w-4 h-4" />
                  <span className="text-sm">{l}</span>
                </label>
              ))}
            </div>
            <div className="mt-3"><label className="text-sm">Weitere Angaben (optional)</label><input className={input} value={data.mittelherkunft_weitere} onChange={(e) => set("mittelherkunft_weitere", e.target.value)} /></div>
            <div className="mt-4"><label className="text-sm">Vermögens-/Herkunftsnachweis (Art des Dokuments)</label><input className={input} placeholder="z. B. Kontoauszug, Erbschein, Lohnabrechnung" value={data.nachweis_art} onChange={(e) => set("nachweis_art", e.target.value)} /></div>

            <div className="text-sm font-semibold text-ink-900 mb-3 mt-8">Politisch exponierte Person (PEP)</div>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer rounded-xl bg-ivory-100 p-4">
                <input type="checkbox" checked={data.pep_keine} onChange={(e) => { set("pep_keine", e.target.checked); if (e.target.checked) set("pep_ja", false); }} className="accent-gold-600 w-5 h-5 mt-0.5" data-testid="input-pep-keine" />
                <div>
                  <div className="font-semibold text-ink-900 text-sm">Ich bin KEINE politisch exponierte Person (PEP)</div>
                  <div className="text-xs text-ink-500 mt-1">Keine der in dieser Anfrage identifizierten Personen ist Regierungsmitglied, hohe/r Beamter/in oder direkte Angehörige einer solchen Person.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer rounded-xl bg-ivory-50 border border-ink-700/10 p-4">
                <input type="checkbox" checked={data.pep_ja} onChange={(e) => { set("pep_ja", e.target.checked); if (e.target.checked) set("pep_keine", false); }} className="accent-gold-600 w-5 h-5 mt-0.5" data-testid="input-pep-ja" />
                <div className="flex-1">
                  <div className="font-semibold text-ink-900 text-sm">Ich bin eine politisch exponierte Person (PEP)</div>
                  <div className="text-xs text-ink-500 mt-1">Die in dieser Anfrage identifizierten Personen gehören zu den folgenden Kategorien:</div>
                  {data.pep_ja && (
                    <input className={input + " mt-2"} placeholder="Namen der Personen" value={data.pep_namen} onChange={(e) => set("pep_namen", e.target.value)} />
                  )}
                </div>
              </label>
            </div>
          </>
        )}

        {/* STEP 8b: Gesprächsprotokoll + Empfehlungen (added inline into next step) */}

        {/* STEP 9: Widerruf */}
        {step_id === "widerruf" && (
          <>
            <StepTitle sub="Optional: Bitte um sofortigen Erwerb.">
              Widerrufsrecht
            </StepTitle>
            <div className="mb-5 text-xs text-ink-500 bg-ivory-100 rounded-xl p-4 flex gap-2">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gold-600" />
              <span>
                Du hast <strong>14 Tage Widerrufsrecht</strong> ohne Angabe von Gründen. Setzt du das Häkchen unten <strong>NICHT</strong>,
                verzichtest du bewusst darauf, um den Erwerb der Edelmetalle sofort auszulösen. Bei einem Widerruf werden
                bereits geleistete Zahlungen zurückerstattet.
              </span>
            </div>
            <div className="rounded-2xl bg-ivory-100 p-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={data.widerrufsverzicht} onChange={(e) => set("widerrufsverzicht", e.target.checked)} className="accent-gold-600 w-5 h-5 mt-0.5" data-testid="widerruf-check" />
                <div>
                  <div className="font-semibold text-ink-900">Ich bitte um sofortigen Erwerb der Edelmetalle</div>
                  <div className="text-sm text-ink-500 mt-1">und verzichte auf mein 14-tägiges Widerrufsrecht bezüglich des Servicevertrags.</div>
                </div>
              </label>
            </div>
            <div className="mt-6 rounded-2xl bg-white border border-ink-700/10 p-5">
              <div className="text-sm font-semibold mb-3">Gesprächsprotokoll</div>
              <div className="text-sm text-ink-700 leading-relaxed mb-4 bg-ivory-100 rounded-xl p-4">
                <strong>Sehr geehrte Klientin, sehr geehrter Klient,</strong><br />
                Sie haben sich für Produkte unseres Hauses entschieden. Wir beglückwünschen Sie zu Ihrer
                Entscheidung und möchten Sie im Unternehmen recht herzlich willkommen heißen. Um Sicherheit
                und Gewissheit zu haben, dass Sie alle Informationen erhalten haben und alles richtig
                erklärt und ausgefüllt worden ist, bitten wir Sie, folgende Punkte zu bestätigen:
              </div>
              <div className="space-y-3">
                {[
                  ["gp_keine_beratung", "Es fand keine Rechts-, Steuer-, Versicherungs- bzw. individuelle Anlagenberatung statt."],
                  ["gp_unterlagen_erhalten", "Ich habe heute alle relevanten Anträge sowie die Widerrufsbelehrung unterzeichnet, alle Vertragsdurchschriften, die dazugehörigen Vertragsunterlagen als auch alle relevanten Informationen erhalten. Ich bestätige, dass mir die Unterlagen schon seit dem Erstgespräch bekannt sind."],
                  ["gp_alle_fragen_beantwortet", "Alle Fragen in den Anträgen, insbesondere zu Risikoparametern, wurden exakt beantwortet, ausschließlich gemäß der Angaben der Klientin/des Klienten."],
                  ["gp_zufrieden_praesentation", "Ich bin mit der Verkaufspräsentation zufrieden gewesen. Der Vermittler hat alle für mich wesentlichen Punkte erläutert."],
                  ["gp_eigener_wunsch", "Das Gespräch hat auf meinen eigenen Wunsch stattgefunden."],
                  ["gp_mindestlaufzeit", "Ich wurde darüber informiert, dass die Laufzeiten in der Regel langfristig, mindestens 12 Jahre, ausgelegt sind. Ich bin mit der vereinbarten Vertragsdauer einverstanden."],
                  ["gp_kosten_hinweis", "Ich bin auf die sofort anfallenden und auch bei vorzeitiger Vertragsbeendigung nicht, auch nicht teilweise, zurück erstattbaren Kosten hingewiesen worden, insbesondere auf:"],
                ].map(([k, l]) => (
                  <label key={k} className="flex items-start gap-3 cursor-pointer text-sm">
                    <input type="checkbox" checked={data[k]} onChange={(e) => set(k, e.target.checked)} className="accent-gold-600 w-4 h-4 mt-0.5 flex-shrink-0" data-testid={k.replace(/_/g,"-")} />
                    <span>{l}</span>
                  </label>
                ))}
                <div className="pl-7 space-y-2 text-sm text-ink-700">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={data.gp_agio_bekannt} onChange={(e) => set("gp_agio_bekannt", e.target.checked)} className="accent-gold-600 w-4 h-4 mt-0.5" data-testid="gp-agio-bekannt" />
                    <span>Agio (wird bei jeder Zahlung belastet)</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={data.gp_einrichtungskosten_bekannt} onChange={(e) => set("gp_einrichtungskosten_bekannt", e.target.checked)} className="accent-gold-600 w-4 h-4 mt-0.5" data-testid="gp-einrichtungskosten-bekannt" />
                    <span>Einrichtungskosten (werden sofort belastet)</span>
                  </label>
                </div>
                <label className="flex items-start gap-3 cursor-pointer text-sm">
                  <input type="checkbox" checked={data.gp_agb_kosten_bestaetigt} onChange={(e) => set("gp_agb_kosten_bestaetigt", e.target.checked)} className="accent-gold-600 w-4 h-4 mt-0.5 flex-shrink-0" data-testid="gp-agb-kosten-bestaetigt"/>
                  <span>Ich bestätige ausdrücklich, über die Kosten entsprechend der AGB („Einrichtungskosten und laufende Kosten") aufgeklärt worden zu sein.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer text-sm">
                  <input type="checkbox" checked={data.gp_rate_finanziell_moeglich} onChange={(e) => set("gp_rate_finanziell_moeglich", e.target.checked)} className="accent-gold-600 w-4 h-4 mt-0.5 flex-shrink-0" data-testid="gp-rate-finanziell-moeglich"/>
                  <span>Die Höhe meiner monatlichen Rate entspricht auch langfristig meinen finanziellen Möglichkeiten. Eine mögliche Einmalanlage stellt keinen wesentlichen Anteil meines Vermögens dar. Die Anlage passt zu dem von mir vorgesehenen Verwendungszweck.</span>
                </label>
              </div>

              <div className="mt-5 pt-5 border-t border-ink-700/10">
                <div className="text-sm font-medium mb-2">Weitere Geschäftspartner (optional)</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <input className={input} placeholder="Name Geschäftspartner" value={data.vp2_name} onChange={(e) => set("vp2_name", e.target.value)} data-testid="input-vp2-name"/>
                  <input className={input} placeholder="GP-Nummer" value={data.vp2_nr} onChange={(e) => set("vp2_nr", e.target.value)} data-testid="input-vp2-nr"/>
                </div>
              </div>
              <div className="mt-4">
                <div className="text-sm font-medium mb-2">Anwesende Personen (optional)</div>
                <input className={input} placeholder="z. B. Ehepartner" value={data.gp_anwesende} onChange={(e) => set("gp_anwesende", e.target.value)} />
              </div>
              <div className="mt-4">
                <div className="text-sm font-medium mb-2">Bemerkungen (optional)</div>
                <textarea rows={2} className={input} placeholder="Freitext-Bemerkungen zum Gespräch" value={data.gp_bemerkungen} onChange={(e) => set("gp_bemerkungen", e.target.value)} data-testid="input-bemerkungen"/>
              </div>

              <div className="mt-5 pt-5 border-t border-ink-700/10">
                <div className="text-sm font-medium mb-2">Wurde Ihnen nahegelegt, Verträge mit anderen Unternehmen zu stornieren?</div>
                <div className="flex gap-6 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="radio" name="storno" checked={data.stornierung_nahegelegt === "ja"} onChange={() => set("stornierung_nahegelegt", "ja")} className="accent-gold-600" data-testid="storno-ja"/> Ja</label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="radio" name="storno" checked={data.stornierung_nahegelegt === "nein"} onChange={() => set("stornierung_nahegelegt", "nein")} className="accent-gold-600" data-testid="storno-nein"/> Nein</label>
                </div>
                {data.stornierung_nahegelegt === "ja" && (
                  <div className="space-y-2">
                    {data.stornierung_produkte.map((sp, i) => (
                      <div key={i} className="grid md:grid-cols-3 gap-2">
                        <input className={input} placeholder="Produkt/Firma" value={sp.produkt} onChange={(e) => { const a=[...data.stornierung_produkte]; a[i]={...a[i], produkt:e.target.value}; set("stornierung_produkte",a); }}/>
                        <input className={input} placeholder="Vertragsart" value={sp.vertragsart} onChange={(e) => { const a=[...data.stornierung_produkte]; a[i]={...a[i], vertragsart:e.target.value}; set("stornierung_produkte",a); }}/>
                        <input className={input} placeholder="Entscheidung (Storno/Beibehalten)" value={sp.entscheidung} onChange={(e) => { const a=[...data.stornierung_produkte]; a[i]={...a[i], entscheidung:e.target.value}; set("stornierung_produkte",a); }}/>
                      </div>
                    ))}
                    <button type="button" onClick={()=> set("stornierung_produkte", [...data.stornierung_produkte, {produkt:"",vertragsart:"",entscheidung:""}])} className="text-xs text-gold-700 underline">+ Weiteren Vertrag hinzufügen</button>
                  </div>
                )}
              </div>
              <div className="mt-4"><label className="text-xs uppercase tracking-widest text-ink-500">Gesprächsdatum</label><input className={input} placeholder="TT.MM.JJJJ" value={data.gesprachsdatum} onChange={(e) => set("gesprachsdatum", e.target.value)} /></div>
            </div>

            <div className="mt-6 rounded-2xl bg-ivory-100 p-5">
              <div className="text-sm font-medium mb-3">Wären Sie bereit, uns weiterzuempfehlen?</div>
              <div className="flex gap-6 mb-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="radio" name="empf" checked={data.weiterempfehlung_ja === true} onChange={() => { set("weiterempfehlung_ja", true); set("weiterempfehlung_bestaetigt", true); }} className="accent-gold-600" data-testid="empf-ja"/> Ja</label>
                <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="radio" name="empf" checked={data.weiterempfehlung_ja === false && data.weiterempfehlung_bestaetigt === false} onChange={() => { set("weiterempfehlung_ja", false); set("weiterempfehlung_bestaetigt", false); }} className="accent-gold-600" data-testid="empf-nein"/> Nein</label>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={data.weiterempfehlung_bestaetigt} onChange={(e) => set("weiterempfehlung_bestaetigt", e.target.checked)} className="accent-gold-600 w-5 h-5 mt-0.5" data-testid="weiterempfehlung" />
                <div>
                  <div className="font-semibold text-sm">Ich bin bereit, Moonshine Solutions weiterzuempfehlen.</div>
                  <div className="text-xs text-ink-500 mt-1">(Optional – trage unten Personen ein, denen wir uns vorstellen dürfen. Bis zu 7 Empfehlungen möglich.)</div>
                </div>
              </label>
              {data.weiterempfehlung_bestaetigt && (
                <div className="mt-4 space-y-6">
                  {data.empfehlungen.map((emp, i) => (
                    <div key={i} className="rounded-xl bg-white border border-ink-700/10 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-xs uppercase tracking-widest text-gold-700 font-semibold">Empfehlung {i + 1}</div>
                        {data.empfehlungen.length > 1 && (
                          <button type="button" onClick={() => set("empfehlungen", data.empfehlungen.filter((_, idx) => idx !== i))} className="text-xs text-red-600 hover:underline" data-testid={`emp-remove-${i}`}>Entfernen</button>
                        )}
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <input className={input} placeholder="Name" value={emp.name} onChange={(e) => { const a = [...data.empfehlungen]; a[i] = { ...a[i], name: e.target.value }; set("empfehlungen", a); }} data-testid={`emp-name-${i}`} />
                        <input className={input} placeholder="Vorname" value={emp.vorname} onChange={(e) => { const a = [...data.empfehlungen]; a[i] = { ...a[i], vorname: e.target.value }; set("empfehlungen", a); }} />
                        <input className={input} placeholder="Alter" value={emp.alter} onChange={(e) => { const a = [...data.empfehlungen]; a[i] = { ...a[i], alter: e.target.value }; set("empfehlungen", a); }} />
                        <input className={input} placeholder="Familienstand" value={emp.familienstand} onChange={(e) => { const a = [...data.empfehlungen]; a[i] = { ...a[i], familienstand: e.target.value }; set("empfehlungen", a); }} />
                        <input className={input} placeholder="Kinder" value={emp.kinder} onChange={(e) => { const a = [...data.empfehlungen]; a[i] = { ...a[i], kinder: e.target.value }; set("empfehlungen", a); }} />
                        <input className={input} placeholder="Beruf" value={emp.beruf} onChange={(e) => { const a = [...data.empfehlungen]; a[i] = { ...a[i], beruf: e.target.value }; set("empfehlungen", a); }} />
                        <input className={input} placeholder="Adresse (Straße + Nr.)" value={emp.adresse} onChange={(e) => { const a = [...data.empfehlungen]; a[i] = { ...a[i], adresse: e.target.value }; set("empfehlungen", a); }} />
                        <input className={input} placeholder="PLZ + Ort" value={emp.adresse2} onChange={(e) => { const a = [...data.empfehlungen]; a[i] = { ...a[i], adresse2: e.target.value }; set("empfehlungen", a); }} />
                        <input className={input} placeholder="Telefon" value={emp.telefon} onChange={(e) => { const a = [...data.empfehlungen]; a[i] = { ...a[i], telefon: e.target.value }; set("empfehlungen", a); }} />
                        <input className={input} placeholder="Hobbys" value={emp.hobbys} onChange={(e) => { const a = [...data.empfehlungen]; a[i] = { ...a[i], hobbys: e.target.value }; set("empfehlungen", a); }} />
                        <input className={input + " md:col-span-2"} placeholder="Gemeinsamkeiten (z. B. Verein, Interesse …)" value={emp.gemeinsamkeiten} onChange={(e) => { const a = [...data.empfehlungen]; a[i] = { ...a[i], gemeinsamkeiten: e.target.value }; set("empfehlungen", a); }} />
                      </div>
                    </div>
                  ))}
                  {data.empfehlungen.length < 7 && (
                    <button
                      type="button"
                      onClick={() => set("empfehlungen", [...data.empfehlungen, { name: "", vorname: "", alter: "", familienstand: "", kinder: "", beruf: "", adresse: "", adresse2: "", telefon: "", hobbys: "", gemeinsamkeiten: "" }])}
                      className="inline-flex items-center gap-2 rounded-full bg-ink-900 hover:bg-ink-700 text-gold-300 px-5 py-2.5 text-sm font-medium transition"
                      data-testid="emp-add"
                    >+ Weitere Empfehlung hinzufügen</button>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* STEP 10: Unterschrift */}
        {step_id === "unterschrift" && (
          <>
            <StepTitle sub="Ort + Datum werden automatisch überall im Vertrag eingesetzt. Deine Unterschrift wird an allen erforderlichen Stellen platziert.">
              Ort, Datum &amp; Unterschrift
            </StepTitle>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div><label className="text-sm">Vertragsnummer (falls bereits vergeben)</label><input className={input} value={data.vertragsnummer} onChange={(e) => set("vertragsnummer", e.target.value)} data-testid="input-vnr" /></div>
              <div><label className="text-sm">Vermittler-Nr (VP-Nr)</label><input className={input} value={data.vp_nr} onChange={(e) => set("vp_nr", e.target.value)} data-testid="input-vpnr" /></div>
              <div><label className="text-sm">Ort *</label><input className={input} value={data.ort} onChange={(e) => set("ort", e.target.value)} data-testid="input-ort" /></div>
              <div><label className="text-sm">Datum *</label><input className={input} value={data.datum} onChange={(e) => set("datum", e.target.value)} data-testid="input-datum" /></div>
            </div>
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-medium">Deine Unterschrift *</div>
              <button type="button" onClick={() => { custSigRef.current?.clear(); setSigDrawn(false); }} className="text-xs text-ink-500 hover:text-red-600 inline-flex items-center gap-1">
                <Eraser className="w-3.5 h-3.5" /> Löschen
              </button>
            </div>
            <div className="rounded-2xl border-2 border-dashed border-gold-500/40 bg-ivory-100 p-2" data-testid="signature-container">
              <SignatureCanvas
                ref={custSigRef}
                penColor="#0F0F0F"
                onEnd={() => setSigDrawn(true)}
                canvasProps={{
                  width: 700,
                  height: 200,
                  style: { width: "100%", height: "200px", display: "block", touchAction: "none", background: "#fff", borderRadius: "0.75rem" }
                }}
              />
            </div>

            <div className="mt-8">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-medium">Vermittler-Unterschrift</div>
                <button type="button" onClick={() => vpSigRef.current?.clear()} className="text-xs text-ink-500 hover:text-red-600 inline-flex items-center gap-1">
                  <Eraser className="w-3.5 h-3.5" /> Löschen
                </button>
              </div>
              <div className="rounded-2xl border-2 border-dashed border-ink-700/20 bg-ivory-50 p-2">
                <SignatureCanvas
                  ref={vpSigRef}
                  penColor="#0F0F0F"
                  canvasProps={{
                    width: 700,
                    height: 120,
                    style: { width: "100%", height: "120px", display: "block", touchAction: "none", background: "#fff", borderRadius: "0.75rem" }
                  }}
                />
              </div>
              <div className="text-xs text-ink-500 mt-2">Wenn der Vermittler später separat signiert, hier leer lassen.</div>
            </div>
          </>
        )}

        {/* STEP 11: Done */}
        {step_id === "done" && (
          <div className="text-center py-6">
            {result ? (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-gold-500 flex items-center justify-center mb-6"><Check className="w-10 h-10 text-ink-900" strokeWidth={3} /></div>
                <h3 className="font-display text-2xl lg:text-3xl mb-3">Vertrag erfolgreich eingereicht!</h3>
                <p className="text-ink-500 mb-8">
                  Das ausgefüllte PDF wurde an <strong>{data.email}</strong> und <strong>Pascal</strong> versendet.
                </p>
                <a href={`${BACKEND_URL}${result.download_url}`} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 rounded-full bg-ink-900 text-gold-300 px-6 py-4 text-base font-medium hover:bg-ink-700 transition"
                   data-testid="download-vertrag-btn">
                  <Download className="w-4 h-4" /> PDF herunterladen
                </a>
                {!result.email_sent && (
                  <div className="mt-6 text-sm text-red-600 flex items-center gap-2 justify-center">
                    <AlertCircle className="w-4 h-4" /> Mailversand fehlgeschlagen ({result.email_error?.slice(0, 80)}). PDF-Download funktioniert.
                  </div>
                )}
              </>
            ) : (
              <div className="text-ink-500">Bitte warten …</div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm flex gap-2" data-testid="wizard-error">
            <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
          </div>
        )}
      </Card>

      {/* Navigation buttons */}
      {step_id !== "done" && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || submitting}
            className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-900 disabled:opacity-30"
            data-testid="wizard-back"
          >
            <ChevronLeft className="w-4 h-4" /> Zurück
          </button>
          {step_id === "unterschrift" ? (
            <button
              onClick={submit}
              disabled={!canNext() || submitting}
              className="inline-flex items-center gap-2 rounded-full bg-gold-500 hover:bg-gold-400 disabled:bg-ink-500/40 text-ink-900 px-6 py-3 font-medium transition disabled:cursor-not-allowed"
              data-testid="wizard-submit"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet …</> : <>Absenden &amp; PDF erstellen<ChevronRight className="w-4 h-4" /></>}
            </button>
          ) : (
            <button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext()}
              className="inline-flex items-center gap-2 rounded-full bg-ink-900 hover:bg-ink-700 disabled:bg-ink-500/40 text-gold-300 px-6 py-3 font-medium transition disabled:cursor-not-allowed"
              data-testid="wizard-next"
            >
              Weiter <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Vertrag;
